import { Injectable } from '@angular/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { CompetentAuthorityModel } from '../../../shared/models/competent-authority.model';
import { CompetentAuthoritiesRepository } from '../../../shared/states/competent-authorities.repository';
import { ContactModel } from '../models/contact.model';
import { ContactRepository } from '../states/contact.repository';
import { ManagementHelperService } from './management-helper.service';

/**
 * Kapselt die zuvor in competent-authority-create-dialog und -edit-dialog duplizierte
 * Logik rund um das Persistieren von Behörden-Kontakten, damit sie auch aus der
 * Tier-Detailansicht (secured-origin-details) wiederverwendet werden kann.
 */
@Injectable({ providedIn: 'root' })
export class AuthorityContactService {
  constructor(
    private readonly _contactRepository: ContactRepository,
    private readonly _authorityRepository: CompetentAuthoritiesRepository,
    private readonly _managementHelper: ManagementHelperService
  ) {
  }

  /**
   * Persistiert einen Kontakt: aktualisiert ihn bei vorhandener id, legt ihn sonst an,
   * sofern er ausfüllbare Werte enthält. Gibt den gespeicherten Kontakt oder null zurück.
   */
  saveContact(contact: ContactModel | null | undefined): Observable<ContactModel | null> {
    if (!contact) {
      return of(null);
    }
    if (contact.id) {
      return this._contactRepository.update(contact, contact.id);
    }
    if (this._managementHelper.checkHasValues(contact)) {
      const { id: _id, ...payload } = contact as any;
      return this._contactRepository.create(payload);
    }
    return of(null);
  }

  /**
   * Legt eine neue Behörde inklusive optionalem Kontakt an.
   */
  createAuthorityWithContact(
    authority: CompetentAuthorityModel,
    contact: ContactModel | null | undefined,
    shelterId: number
  ): Observable<CompetentAuthorityModel> {
    return this.saveContact(contact).pipe(
      switchMap(savedContact => {
        const payload = {
          ...authority,
          animalShelter: { id: shelterId },
          contacts: savedContact?.id ? [{ id: savedContact.id }] : []
        } as CompetentAuthorityModel;
        return this._authorityRepository.createCompetentAuthority(payload);
      })
    );
  }

  /**
   * Hängt einen (neuen oder bestehenden) Kontakt an eine Behörde und speichert diese.
   * Gibt die aktualisierte Behörde sowie den gespeicherten Kontakt zurück.
   */
  attachContactToAuthority(
    authority: CompetentAuthorityModel,
    contact: ContactModel
  ): Observable<{ authority: CompetentAuthorityModel; contact: ContactModel | null }> {
    return this.saveContact(contact).pipe(
      switchMap(savedContact => {
        if (!savedContact?.id) {
          return of({ authority, contact: savedContact });
        }
        const existingContacts = (authority.contacts ?? []).filter(entry => entry?.id !== savedContact.id);
        const payload = {
          ...authority,
          contacts: [...existingContacts, { id: savedContact.id }]
        } as CompetentAuthorityModel;
        return this._authorityRepository.updateCompetentAuthority(authority.id, payload).pipe(
          map(updatedAuthority => ({ authority: updatedAuthority, contact: savedContact }))
        );
      })
    );
  }
}
